import { toastr } from "react-redux-toastr";
import { useForm } from "../../../hooks/useForm";
import {
  useAsignarTramitesMutation,
  useBuscarTramiteMutation,
  useGetBusquedaQuery,
  useGetTramitesSinAsignarQuery,
  useLazyGetTramitesSinAsignarQuery,
} from "../../../services/docs";
import { Spinner } from "../../../components/Spinner";
import { TablaTramitesComponent } from "../../../components/TablaTramitesComponent";

export const AsignarTramitesScreen = () => {
  // Queries & Mutations
  const { data: dataBusqueda, isLoading: loadingBusqueda } = useGetBusquedaQuery();
  const { data: dataSinAsignar, isLoading: loadingSinAsignar } = useGetTramitesSinAsignarQuery();
  const [reenviarListaSinAsignar] = useLazyGetTramitesSinAsignarQuery();

  const [buscarTramiteAction, { data: siniestros, isLoading: loadingBuscar }] = useBuscarTramiteMutation();
  const [asignarTramiteAction, { isLoading: cargaAsignar }] = useAsignarTramitesMutation();

  // Custom Form Hook
  const { formulario, handleInputChange, handleSelectChange } = useForm({
    fi_busqueda_id: "",
    fc_valor: "",
  });

  // Función: Buscar Trámite
  const buscarTramite = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!formulario.fi_busqueda_id || +formulario.fi_busqueda_id === 0) {
      toastr.error("Debe seleccionar un tipo de búsqueda");
      return;
    }
    if (!formulario.fc_valor || formulario.fc_valor.trim() === "") {
      toastr.error("Debe agregar un valor de búsqueda");
      return;
    }

    try {
      await buscarTramiteAction({
        ...formulario,
        fi_busqueda_id: +formulario.fi_busqueda_id,
      }).unwrap();
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error("Ocurrió un error al realizar la búsqueda");
      }
    }
  };

  // Función: Asignar Trámite
  const asignarTramite = async (t: any, eje: any) => {
    if (!eje) {
      toastr.error("Debe seleccionar un Ejecutivo de la lista");
      return;
    }

    try {
      const resp = await asignarTramiteAction({
        fc_sub_id: eje,
        fi_tramite_id: +t.t_id,
      }).unwrap();

      if (resp) {
        toastr.success(resp.message || "Trámite asignado correctamente");
        reenviarListaSinAsignar(); // Refrescar la tabla de no asignados
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error(err.data.message);
      }
    }
  };

  const isGlobalLoading = loadingBusqueda || loadingSinAsignar;

  if (isGlobalLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10">

      {/* =========================================
          SECCIÓN 1: BÚSQUEDA Y REASIGNACIÓN
          ========================================= */}
      <section className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-5">
          <h1 className="flex items-center gap-2 text-lg font-bold tracking-tight text-slate-900">
            <svg className="h-5 w-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            Búsqueda y Reasignación de Trámites
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Busca un siniestro específico en curso para reasignarlo a un nuevo ejecutivo.
          </p>
        </div>

        {/* Formulario de Búsqueda */}
        <form onSubmit={buscarTramite} className="bg-slate-50/50 p-6">
          <div className="grid grid-cols-1 items-end gap-5 lg:grid-cols-12">

            <div className="lg:col-span-4">
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-600" htmlFor="fi_busqueda_id">
                Criterio de Búsqueda
              </label>
              <select
                id="fi_busqueda_id"
                name="fi_busqueda_id"
                onChange={handleSelectChange}
                value={formulario.fi_busqueda_id}
                className="h-11 w-full cursor-pointer rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 transition-all hover:bg-slate-50 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              >
                <option value="" disabled>Seleccione filtro...</option>
                {dataBusqueda?.busqueda?.map((b: any) => (
                  <option key={b.id} value={b.id}>
                    {b.fc_nombre_busqueda}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-5">
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-600" htmlFor="fc_valor">
                Valor a buscar
              </label>
              <div className="relative">
                <input
                  id="fc_valor"
                  name="fc_valor"
                  type="text"
                  placeholder="Ingrese el folio o valor..."
                  onChange={handleInputChange}
                  value={formulario.fc_valor}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            <div className="lg:col-span-3">
              <button
                type="submit"
                disabled={loadingBuscar}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-brand-700 disabled:opacity-70"
              >
                {loadingBuscar ? <Spinner /> : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                    Buscar Trámite
                  </>
                )}
              </button>
            </div>

          </div>
        </form>

        {/* Resultados de la Búsqueda */}
        {loadingBuscar && (
          <div className="flex w-full py-10 justify-center">
            <Spinner />
          </div>
        )}

        {!loadingBuscar && siniestros?.busquedas && siniestros.busquedas.length > 0 && (
          <div className="border-t border-slate-100 p-4">
            <h3 className="mb-3 px-2 text-sm font-bold text-slate-800">Resultados Encontrados</h3>
            <TablaTramitesComponent
              data={siniestros.busquedas}
              texto_accion="Asignar"
              accion={asignarTramite}
              ejecutivos={dataSinAsignar?.ejecutivos || []}
              cargando={cargaAsignar}
            />
          </div>
        )}

        {!loadingBuscar && siniestros?.busquedas && siniestros.busquedas.length === 0 && (
          <div className="p-6">
            <div className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-700">
              <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
              <span className="text-sm font-semibold">No se encontraron siniestros que coincidan con la búsqueda.</span>
            </div>
          </div>
        )}
      </section>

      {/* =========================================
          SECCIÓN 2: BANDEJA SIN ASIGNAR
          ========================================= */}
      <section className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50/50 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-bold tracking-tight text-slate-900">
              <svg className="h-5 w-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
              Bandeja de Trámites sin Asignar
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Asigna folios nuevos directamente a los ejecutivos disponibles.
            </p>
          </div>
          {dataSinAsignar?.tramites && dataSinAsignar.tramites.length > 0 && (
            <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-brand-700">
              {dataSinAsignar.tramites.length} Pendientes
            </span>
          )}
        </div>

        <div className="p-4 sm:p-6">
          {dataSinAsignar?.tramites && dataSinAsignar.tramites.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-emerald-200 bg-emerald-50 py-16 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
              </div>
              <h3 className="text-xl font-bold text-emerald-900">¡Bandeja de entrada limpia!</h3>
              <p className="mt-2 text-sm text-emerald-700">No hay trámites nuevos pendientes de asignación en este momento.</p>
            </div>
          ) : (
            <TablaTramitesComponent
              data={dataSinAsignar?.tramites || []}
              texto_accion="Asignar"
              accion={asignarTramite}
              ejecutivos={dataSinAsignar?.ejecutivos || []}
              cargando={cargaAsignar}
            />
          )}
        </div>
      </section>

    </div>
  );
};
